import { useCallback, useLayoutEffect, useState, type RefObject } from "react"

type PanelPosition = {
  top?: number
  bottom?: number
  left?: number
  width?: number
  maxHeight?: number
}

export function useFloatingPanelPosition(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  minimumWidth = 224
): PanelPosition {
  const [panelStyle, setPanelStyle] = useState<PanelPosition>({})

  const positionPanel = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) {
      return
    }

    const rect = trigger.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) {
      return
    }

    const gutter = 6
    const viewportPadding = 8
    const desiredWidth = Math.max(rect.width, minimumWidth)
    const width = Math.min(desiredWidth, window.innerWidth - viewportPadding * 2)
    const left = Math.min(
      Math.max(rect.left, viewportPadding),
      window.innerWidth - width - viewportPadding
    )
    const spaceBelow = window.innerHeight - rect.bottom - gutter - viewportPadding
    const spaceAbove = rect.top - gutter - viewportPadding
    const openAbove = spaceBelow < 200 && spaceAbove > spaceBelow
    const next = openAbove
      ? { bottom: window.innerHeight - rect.top + gutter, left, width, maxHeight: spaceAbove }
      : { top: rect.bottom + gutter, left, width, maxHeight: spaceBelow }

    setPanelStyle((previous) =>
      previous.top === next.top &&
      previous.bottom === next.bottom &&
      previous.left === next.left &&
      previous.width === next.width &&
      previous.maxHeight === next.maxHeight
        ? previous
        : next
    )
  }, [triggerRef, minimumWidth])

  // Visibility and a parent menu's placement can change without a window event.
  // Measure after each commit; equal geometry retains the existing state.
  useLayoutEffect(() => {
    if (open) {
      positionPanel()
    }
  })

  useLayoutEffect(() => {
    if (!open) {
      return
    }

    const observer = new ResizeObserver(positionPanel)
    if (triggerRef.current) {
      observer.observe(triggerRef.current)
    }
    window.addEventListener("resize", positionPanel)
    window.addEventListener("scroll", positionPanel, true)
    window.addEventListener("animationend", positionPanel, true)
    window.addEventListener("transitionend", positionPanel, true)
    return () => {
      observer.disconnect()
      window.removeEventListener("resize", positionPanel)
      window.removeEventListener("scroll", positionPanel, true)
      window.removeEventListener("animationend", positionPanel, true)
      window.removeEventListener("transitionend", positionPanel, true)
    }
  }, [open, positionPanel, triggerRef])

  return panelStyle
}
